import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActionService } from '../../services/action.service'
import { filter, map, mergeMap, switchMap, tap } from 'rxjs/operators'
import { FileUploadDownloadService } from '../../../../services/file-upload-download.service'
import { of, Subscription } from 'rxjs'
import { DialogService } from 'primeng/dynamicdialog'
import { ScriptResultDialogComponent } from '../../dialogs/script-result-dialog/script-result-dialog.component'
import { NotificationService } from '../../../../services/notification.service'
import { DashboardPreviewDialogComponent } from '../../dialogs/dashboard-preview-dialog/dashboard-preview-dialog.component'
import { DialogCleanupService } from 'projects/jml-lib/src/lib/services/dialog-cleanup.service'
import { WizardComponent } from '../../../wizard/components/wizard.component'
import { WizardService } from '../../../wizard/services/wizard.service'

/**
 * Component receives all information that are required to execute an action from the action service. It will subscribe to the actionQueue and show the output if necessary.
 *
 * How does it work:
 * - subscription to actionQueue$ happens onInit
 * - result is shown in scriptResultDialog
 * - if action is configured to show the result a popup appears
 * - in case of result type TEMP_FILE the file download is triggered
 */

@Component({
  selector: 'jml-lib-script-result',
  templateUrl: './script-result.component.html',
  styleUrls: ['./script-result.component.scss'],
  providers: [DialogService]
})
export class ScriptResultComponent implements OnInit, OnDestroy {
  private actionQueueSubscription: Subscription

  constructor(
    private fileService: FileUploadDownloadService,
    private actionService: ActionService,
    private dialogService: DialogService,
    private dialogCleanupService: DialogCleanupService,
    private wizardService: WizardService
  ) {}

  ngOnInit() {
    this.actionQueueSubscription = this.actionService.actionQueue$
      .pipe(
        mergeMap((action$) => action$),
        filter((action) => !!action && !!action.actionDetails && !!action.response),
        tap((action) => {
          const actionDetails = action.actionDetails
          const actionResponse = action.response
          if (actionDetails.showResult) {
            this.dialogCleanupService.registerDialog(
              this.dialogService.open(ScriptResultDialogComponent, {
                header: `${actionDetails.name} ausgeführt`,
                draggable: true,
                width: '80%',
                data: {
                  actionResponse
                }
              })
            )
          }
        }),
        map((action) => action.response),
        switchMap((response) => {
          if (response['resultType'] === 'TEMP_FILE') {
            return this.fileService.getTempFile(response['result']).pipe(
              tap((data) => {
                const type = data.headers.get('content-type') // get content type, (e.g. application/pdf) and fileName
                let fileName = data.headers.get('content-filename')
                fileName = fileName.substring(1, fileName.length - 1)
                const blob = new Blob([data.body], { type: type }) // define blob and set filetype
                const downloadURL = URL.createObjectURL(blob) // create temporary download link and click it
                let link = document.createElement('a')
                link.href = downloadURL
                if (type === 'application/pdf') {
                  // If Type = application/pdf --> open pdf in new tab in Browser
                  link.download
                  link.target = '_blank'
                } else {
                  link.download = fileName
                }
                link.click()
              })
            )
          } else if (response['resultType'] === 'LAYOUT') {
            const result = JSON.parse(response['result'])
            this.dialogCleanupService.registerDialog(
              this.dialogService.open(DashboardPreviewDialogComponent, {
                header: 'Dashboard Preview',
                draggable: true,
                width: '80%',
                styleClass: 'dashboard-preview-dialog',
                data: {
                  layout: result['layout'],
                  entityReference: result['entityReference']
                }
              })
            )
          } else if (response['resultType'] === 'WIZARD') {
            this.wizardService.openWizard(response['result'])
          }
          return of(undefined)
        })
      )
      .subscribe()
  }

  ngOnDestroy() {
    this.actionQueueSubscription.unsubscribe()
  }
}
